import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

import { inputDefaultProps, inputPropTypes } from '../../props/inputProps';

import { applyEventListener, useCombinedRefs, Validation } from '../../helpers';
import { formatDate } from '../utility';
import { IconBack, IconCalendarGrid, IconNext } from '@optic-delight/icons';
import ReactDatePicker from 'react-datepicker';
import { Button } from '../button';
import Input from './Input';
import { useFormContext } from 'react-hook-form';
import { DatePickerHotKeys } from '../../helpers/keyboardKeys';

function getDate(offsetDays = 0) {
	let aDate = new Date(Date.now());
	aDate.setDate(aDate.getDate() + offsetDays);
	return aDate;
}

function addMonths(date = getDate(), offsetMonths = 0) {
	const aDate = new Date(date.getTime());
	aDate.setMonth(aDate.getMonth() + offsetMonths);
	return aDate;
}

export function parseDateString(dateString) {
	if (!dateString) {
		return undefined;
	}

	let parts = dateString.split('/');
	const date = new Date(parts[2], parts[0] - 1, parts[1]);

	return isNaN(date.getTime()) ? undefined : date;
}

const DatePicker = React.forwardRef(
	(
		{
			id,
			name,
			label,
			button,
			tooltip,
			helptext = !button && 'MM/DD/YYYY',
			defaultValue,
			value,
			onChange,
			onDatePickerChange,
			required,
			dirty,
			offset,
			validators,
			groupClassName,
			'aria-label': ariaLabel,
			inline,
			buttonTitle = 'Select date',
			'data-testid': dataTestId,
			embedded,
			addOnPrepend,
			addOnAppend,
			...props
		},
		ref
	) => {
		const inputHiddenClass = [button && 'd-none'].filter(Boolean).join('');
		if (props.excludeDates) {
			props.excludeDates = props.excludeDates.length ? props.excludeDates.map(date => parseDateString(date)) : parseDateString(props.excludeDates);
		}
		if (props.minDate) {
			props.minDate = parseDateString(props.minDate);
		}
		const [startDate, setStartDate] = useState(parseDateString(value || defaultValue));
		const inputRef = useCombinedRefs(useRef(), ref);
		const formContext = useFormContext() || {};

		const setDate = useCallback(
			date => {
				const dateWithLeadingZeros = ('0' + (date.getMonth() + 1)).slice(-2) + '/' + ('0' + date.getDate()).slice(-2) + '/' + date.getFullYear();
				setStartDate(date);
				inputRef.current.value = dateWithLeadingZeros;
				applyEventListener(onDatePickerChange, [inputRef.current]);
			},
			[inputRef, onDatePickerChange]
		);

		const handleChange = useCallback(
			e => {
				const newDate = parseDateString(e.target.value);
				setStartDate(newDate || undefined);
				inputRef.current.value = e.target.value;

				applyEventListener(onChange, [e]);
			},
			[inputRef, onChange]
		);

		const handleBlur = useCallback(
			e => {
				let targetValue = e.target.value;
				if (targetValue === DatePickerHotKeys.today) {
					setDate(getDate());
					e.target.value = inputRef.current.value;
					applyEventListener(onChange, [e]);
				} else if (targetValue === DatePickerHotKeys.previousDay) {
					setDate(getDate(-1));
					e.target.value = inputRef.current.value;
					applyEventListener(onChange, [e]);
				} else if (targetValue === DatePickerHotKeys.nextDay) {
					setDate(getDate(1));
					e.target.value = inputRef.current.value;
					applyEventListener(onChange, [e]);
				} else {
					e.target.value = formatDate(e.target.value);
					handleChange(e);
				}

				applyEventListener(props.onBlur, [e]);
			},
			[setDate, inputRef, props.onBlur, onChange, handleChange]
		);

		return (
			<Input
				ref={inputRef}
				groupClassName={groupClassName}
				id={id}
				name={name}
				label={label}
				aria-label={ariaLabel}
				aria-hidden={button}
				tooltip={tooltip}
				helptext={helptext}
				value={value}
				defaultValue={defaultValue}
				onBlur={handleBlur}
				onChange={handleChange}
				required={required}
				disabled={props.disabled}
				dirty={dirty}
				validators={validators}
				maxLength="10"
				size="10"
				className={inputHiddenClass}
				data-testid={dataTestId}
				inline={inline}
				embedded={embedded}
				addOnPrepend={embedded && addOnPrepend}
				addOnAppend={
					<>
						{!embedded &&
							addOnAppend &&
							(Array.isArray(addOnAppend) ? (
								addOnAppend.map((addOn, index) => (
									<span key={index} className="input-group-text">
										{addOn}
									</span>
								))
							) : (
								<span className="input-group-text">{addOnAppend}</span>
							))}
						<ReactDatePicker
							id={id + '_picker'}
							selected={startDate}
							onChange={date => {
								setDate(date);
								if (formContext?.setValue) {
									formContext.setValue(inputRef.current.name, inputRef.current.value, { shouldValidate: true });
								}
							}}
							onChangeRaw={e => {
								if (!e.target.value) {
									return;
								}
								handleChange(e);
							}}
							preventOpenOnFocus
							dateFormat="MM/dd/yyyy"
							showLeadingZeros={true}
							customInput={
								!embedded ? (
									button ? (
										<Button iconDirection="append" icon="calendar-grid">
											<span className="me-2">{startDate ? formatDate(startDate) : buttonTitle}</span>
										</Button>
									) : (
										<Button icon="calendar-grid" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
											<span className="visually-hidden">{`choose date${
												startDate ? ', selected date is ' + formatDate(startDate, { dateStyle: 'full' }) : ''
											}`}</span>
										</Button>
									)
								) : (
									<span className="input-group-text">
										<IconCalendarGrid />
									</span>
								)
							}
							popperPlacement="bottom"
							popperModifiers={[
								{
									name: 'offset',
									options: {
										enabled: true,
										offset: offset
									}
								}
							]}
							renderCustomHeader={({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => {
								const monthYearFormat = { year: 'numeric', month: 'long' };
								return (
									<div className="clearfix">
										<button
											type="button"
											className="btn btn-link float-start previous"
											onClick={decreaseMonth}
											disabled={prevMonthButtonDisabled}
											aria-label={`Previous Month, ${formatDate(addMonths(date, -1), monthYearFormat)}`}>
											<IconBack />
										</button>
										<span className="current-month">{formatDate(date, monthYearFormat)}</span>
										<button
											type="button"
											className="btn btn-link float-end next"
											onClick={increaseMonth}
											disabled={nextMonthButtonDisabled}
											aria-label={`Next Month, ${formatDate(addMonths(date, 1), monthYearFormat)}`}>
											<IconNext />
										</button>
									</div>
								);
							}}
							{...props}
						/>
						{!embedded &&
							addOnPrepend &&
							(Array.isArray(addOnPrepend) ? (
								addOnPrepend.map((addOn, index) => (
									<span key={index} className="input-group-text">
										{addOn}
									</span>
								))
							) : (
								<span className="input-group-text">{addOnPrepend}</span>
							))}
					</>
				}
			/>
		);
	}
);
DatePicker.displayName = 'DatePicker';

DatePicker.propTypes = {
	...inputPropTypes,

	/**
	 * Input field value.  If used, must have an onChange event for it
	 */
	value: PropTypes.any,

	/**
	 * input onChange event
	 */
	onChange: PropTypes.func,

	/**
	 * date picker onChange event.  returns the input reference
	 */
	onDatePickerChange: PropTypes.func,

	/**
	 * input onBlur event
	 */
	onBlur: PropTypes.func,

	/**
	 * popper offset
	 */
	offset: PropTypes.array,

	/**
	 * testing utility helper
	 */
	'data-testid': PropTypes.string,

	/**
	 * Title for button mode
	 */
	buttonTitle: PropTypes.string,

	/**
	 * Render button
	 */
	button: PropTypes.bool
};

DatePicker.defaultProps = {
	...inputDefaultProps,
	offset: [-100, 0],
	validators: [Validation.date]
};

export default DatePicker;
